// Lead Sales — the lead-sale proxy admin surface (gated: sec-lead-sales).
//
// READ + CONFIG only. This page surfaces what the proxy is doing (unique-lead
// stats: the ledger-backed outcomes, a separate do-not-resell held-back tally,
// considered + transient skips, by-destination / blocked-by-buyer, and a
// recent-sales list) and lets an operator edit its operating config +
// flip it on/off. It never touches the proxy's dispatch / exclusive-gate /
// routing / claim logic — those live in rtb-api and are structural (the
// exclusive gate fails CLOSED; the no-double-sell mutex is a phone-keyed
// conditional write). The proxy is a scheduled sibling of the dialer; this page
// is a near-exact clone of Dialer.tsx plus three array editors.
//
// Two data planes, deliberately separate:
//   - LIVE (stats + recent sales): windowed, auto-refreshed every 15s (the
//     Dialer poll pattern; pauses while the tab is hidden). The window picker
//     defaults to "Today (PT)" — passing no window lets the server use its
//     Pacific-midnight default (api.leadSalesStats()).
//   - CONFIG (the lead_sale_config singleton) + the seen-buyers picker list:
//     loaded once, mutated via the master on/off toggle and the config form.
//
// 🔒 Live-safety: turning Lead Sales ON starts REAL selling of leads to
// LeadExec, so the master toggle requires a deliberate confirm step to enable
// (Off is immediate). The timing band (wait/max_age) sits under an "Advanced"
// disclosure with a client-side guard (max_age must exceed wait) + the 5-minute
// settle warning (a too-short wait can sell a lead a buyer is about to buy
// exclusively — TrackDrive revenue/buyer can post minutes after the call),
// mirroring the server's 400 invalid_band. sales_per_min is capped at the
// dispatcher's per-tick ceiling. Each destination's api-key is paste-only: the
// operator pastes the raw key, admin-api writes it to Secrets Manager and returns
// only the ARN + a masked hint (the key value never comes back to this screen).
// `enabled` is owned by the master toggle, never a config Save (a stale form
// can't re-enable a killed proxy).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";

import {
  api,
  ApiError,
  type LeadSaleConfig,
  type LeadSaleConfigResponse,
  type LeadSaleRoutingTier,
  type LeadSaleRow,
  type LeadSaleStats,
  type RoutingRule,
  type SeenBuyer,
  type SellDestination,
} from "../lib/api";
import {
  buttonStyle,
  Card,
  EmptyState,
  FieldLabel,
  inputStyle,
  PageHeader,
  Pill,
  type PillTone,
  Skeleton,
  StatCard,
  tokens,
} from "../lib/theme";

const POLL_MS = 15_000;
const SALES_LIMIT = 100;
// Fallback per-tick sell ceiling shown before the config (with the real
// tick_cap) loads. sales_per_min may rise to the cap, never past it.
const TICK_CAP_FALLBACK = 60;
// Default LeadExec insert endpoint pre-filled when adding a destination (PRP).
const LEADEXEC_DEFAULT_URL = "https://leads.leadexec.net/v2/insert/general";

// "Today (PT)" is the default selected window and a special sentinel: it sends
// NO window param so the server applies its Pacific-midnight-to-now default
// (api.leadSalesStats() with no args). The other presets are rolling-window seconds.
const TODAY_PT = -1;

const PRESETS: Array<{ label: string; sec: number }> = [
  { label: "Today (PT)", sec: TODAY_PT },
  { label: "Last 15 min", sec: 15 * 60 },
  { label: "Last 30 min", sec: 30 * 60 },
  { label: "Last hour", sec: 3600 },
  { label: "Last 3 hours", sec: 3 * 3600 },
  { label: "Last 6 hours", sec: 6 * 3600 },
  { label: "Last 24 hours", sec: 24 * 3600 },
  { label: "Last 7 days", sec: 7 * 24 * 3600 },
  { label: "Last 30 days", sec: 30 * 24 * 3600 },
];

// The tiers a routing rule may target (mirrors the server enum + "any").
const TIER_OPTIONS: Array<{ value: LeadSaleRoutingTier; label: string }> = [
  { value: "any", label: "Any tier" },
  { value: "below_10k", label: "Under $10k" },
  { value: "over_10k", label: "$10k–$20k" },
  { value: "over_20k", label: "$20k–$30k" },
  { value: "over_30k", label: "$30k–$40k" },
  { value: "over_40k", label: "$40k+" },
];

// The window params for api.leadSalesStats — an explicit epoch range, a rolling
// window (seconds), or `undefined` (→ server Pacific-Today default). Matches the
// server's precedence: start/end wins, else window, else the default.
type StatsOpts = { windowSec?: number; start?: number; end?: number };

/** Resolve the selected preset/custom into api.leadSalesStats opts. */
function toStatsOpts(
  presetSec: number,
  custom: { start: string; end: string } | null,
): StatsOpts | undefined {
  if (custom && custom.start && custom.end) {
    const start = Math.floor(new Date(custom.start + "T00:00:00Z").getTime() / 1000);
    const end = Math.floor(new Date(custom.end + "T23:59:59Z").getTime() / 1000);
    return { start, end };
  }
  if (presetSec === TODAY_PT) return undefined; // server default = Pacific Today
  return { windowSec: presetSec };
}

function fmtCount(n: number): string {
  return (n ?? 0).toLocaleString();
}

function fmtCents(cents: number | null): string {
  if (cents === null || cents === undefined || Number.isNaN(cents)) return "—";
  return `$${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// Render a sale timestamp (epoch ms) in Pacific, matching the PT reporting model
// used across the dashboards.
function fmtSaleTime(ms: number): string {
  if (!ms) return "—";
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return "—";
  return (
    d.toLocaleString([], {
      timeZone: "America/Los_Angeles",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }) + " PT"
  );
}

// A stored tier code → operator-facing label. Unknown values pass through so the
// table never blanks on an unexpected tier.
function prettyTier(tier: string): string {
  switch (tier) {
    case "over_40k":
      return "$40k+";
    case "over_30k":
      return "$30k–$40k";
    case "over_20k":
      return "$20k–$30k";
    case "over_10k":
      return "$10k–$20k";
    case "below_10k":
      return "Under $10k";
    default:
      return tier || "—";
  }
}

// Map a sale status to a Pill tone. Substring-matched so it tolerates whatever
// exact status the lead-sales row carries (selling / sold / blocked_exclusive /
// no_route / rejected / failed).
function statusTone(status: string): PillTone {
  const s = status.toLowerCase();
  if (s.includes("block")) return "warning"; // blocked_exclusive
  if (s.includes("fail")) return "danger";
  if (s.includes("reject")) return "warning";
  if (s.includes("route")) return "warning"; // no_route
  if (s.includes("sold")) return "success";
  return "neutral"; // selling / unknown
}

// Friendly labels for the ledger-backed OUTCOMES — one row per lead, its final
// decision. These are unique and RECONCILE (they sum to "leads decided this
// window"). blocked_exclusive + no_route are owned here, NOT in the transient
// skips below, so they're never double-counted.
const OUTCOME_LABELS: Array<{
  key: keyof LeadSaleStats["outcomes"];
  label: string;
  tone?: PillTone;
}> = [
  { key: "sold", label: "Sold", tone: "success" },
  { key: "rejected", label: "Rejected", tone: "warning" },
  { key: "no_route", label: "No route", tone: "warning" },
  { key: "blocked_exclusive", label: "Blocked — exclusive", tone: "warning" },
  { key: "failed", label: "Failed", tone: "danger" },
];

// PRP witzzy_sequential AC17 — the four Witzzy ledger outcomes, rendered as
// tiles alongside the five above (same Breakdown, same card). The keys are
// OPTIONAL on LeadSaleStats.outcomes (a response from before this stage
// shipped carries none of them), so the map below reads each with `?? 0`.
const WITZZY_OUTCOME_LABELS: Array<{
  key: keyof LeadSaleStats["outcomes"];
  label: string;
  tone?: PillTone;
}> = [
  { key: "witzzy_sent", label: "Witzzy pending" },
  { key: "witzzy_converted", label: "Witzzy converted", tone: "success" },
  { key: "witzzy_expired", label: "Witzzy expired", tone: "warning" },
  { key: "witzzy_send_failed", label: "Witzzy send failed", tone: "danger" },
];

// Transient skips — non-final, retried reasons (the lead is looked at again on a
// later tick), deduped per lead. already_sold is NOT here (it's the separate
// do-not-resell held-back tally); blocked_exclusive / no_route are OUTCOMES.
const TRANSIENT_SKIP_LABELS: Array<{
  key: keyof LeadSaleStats["transient_skips"];
  label: string;
}> = [
  { key: "invalid_phone", label: "Invalid phone" },
  { key: "exclusive_check_failed", label: "Exclusive check failed (fail-closed)" },
  { key: "claim_lost", label: "Claim lost (race)" },
  { key: "lead_error", label: "Lead error" },
];

// ---------------------------------------------------------------------------
// Config form state. Numeric fields (rates, minutes, weights, vid/aid/lid) are
// kept as strings so the inputs can be cleared mid-edit; they're parsed +
// validated on Save. `enabled` is NOT here — the master toggle owns it; Save
// carries the current enabled through. Each destination has a STABLE id (the
// routing rules reference it); each rule carries a local-only `id` for React
// list stability that is stripped before the config is sent.
// ---------------------------------------------------------------------------

interface DestDraft {
  id: string;
  name: string;
  enabled: boolean;
  // Only buy leads that have NOT called TrackDrive since the form-fill.
  skip_if_called: boolean;
  url: string;
  // The STORED secret ARN (round-tripped, display-only) + its masked hint. The
  // key VALUE never lives here — the operator pastes a new/replacement key into
  // `api_key_new` (write-only; sent as api_key_new on Save only when non-empty).
  api_key_secret_arn: string;
  api_key_hint?: string;
  api_key_new: string;
  vid: string;
  aid: string;
  lid: string;
}

interface RuleDraft {
  id: string; // local-only React key (NOT sent)
  tier: LeadSaleRoutingTier;
  destination_id: string;
  weight: string;
}

interface FormState {
  waitMin: string;
  maxAgeMin: string;
  salesPerMin: string;
  destinations: DestDraft[];
  routing_rules: RuleDraft[];
  exclusive_buyer_ids: number[];
}

/** Short, session-stable id for a new destination / rule row. */
function shortId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

function newDestDraft(): DestDraft {
  return {
    id: shortId(),
    name: "",
    enabled: true,
    skip_if_called: false,
    url: LEADEXEC_DEFAULT_URL,
    api_key_secret_arn: "",
    api_key_new: "",
    vid: "",
    aid: "",
    lid: "",
  };
}

function configToForm(c: LeadSaleConfig): FormState {
  return {
    waitMin: String(c.wait_ms / 60000),
    maxAgeMin: String(c.max_age_ms / 60000),
    salesPerMin: String(c.sales_per_min),
    destinations: c.destinations.map((d) => ({
      id: d.id,
      name: d.name,
      enabled: d.enabled,
      // Back-compat: a stored destination predating this field has no
      // skip_if_called — treat missing as false (the server's default).
      skip_if_called: d.skip_if_called ?? false,
      url: d.url,
      api_key_secret_arn: d.api_key_secret_arn,
      // exactOptionalPropertyTypes: only carry the hint key when it's present.
      ...(d.api_key_hint !== undefined ? { api_key_hint: d.api_key_hint } : {}),
      api_key_new: "", // paste field starts empty; a new key is opt-in per Save
      vid: String(d.source.vid),
      aid: String(d.source.aid),
      lid: String(d.source.lid),
    })),
    routing_rules: c.routing_rules.map((r) => ({
      id: shortId(),
      tier: r.tier,
      destination_id: r.destination_id,
      weight: String(r.weight),
    })),
    exclusive_buyer_ids: [...c.exclusive_buyer_ids],
  };
}

/** Parse + validate the form into a clean LeadSaleConfig, or return a message.
 *  `enabled` is carried through (the master toggle owns it). Mirrors the
 *  server's guards so most 400s are prevented client-side (band + routing). */
function buildConfigFromForm(
  form: FormState,
  enabled: boolean,
  tickCap: number,
): { config: LeadSaleConfig } | { error: string } {
  const spm = Number(form.salesPerMin);
  if (form.salesPerMin.trim() === "" || !Number.isFinite(spm) || spm <= 0) {
    return { error: "Sales/min must be a positive number." };
  }
  if (spm > tickCap) {
    return { error: `Sales/min is capped at ${tickCap}.` };
  }
  const wMin = Number(form.waitMin);
  const aMin = Number(form.maxAgeMin);
  if (form.waitMin.trim() === "" || !Number.isFinite(wMin) || wMin <= 0) {
    return { error: "Wait must be a positive number of minutes." };
  }
  if (form.maxAgeMin.trim() === "" || !Number.isFinite(aMin) || aMin <= 0) {
    return { error: "Max age must be a positive number of minutes." };
  }
  const wait_ms = Math.round(wMin * 60000);
  const max_age_ms = Math.round(aMin * 60000);
  if (max_age_ms <= wait_ms) {
    return {
      error:
        "Max age must be greater than wait — otherwise the eligibility band is empty and the proxy sells nobody.",
    };
  }

  // Destinations — every field required; vid/aid/lid must be finite numbers.
  const destinations: SellDestination[] = [];
  for (const [i, d] of form.destinations.entries()) {
    const label = d.name.trim() || `#${i + 1}`;
    if (d.name.trim() === "") return { error: `Destination ${label}: name is required.` };
    if (d.url.trim() === "") return { error: `Destination ${label}: URL is required.` };
    // A destination must have a key: either one already on file (a stored ARN) or
    // a raw key pasted now (which admin-api writes to Secrets Manager, then returns
    // the ARN). The raw ARN is no longer typed here — the key is paste-only.
    const apiKeyNew = d.api_key_new.trim();
    const hasStoredKey = d.api_key_secret_arn.trim() !== "";
    if (!hasStoredKey && apiKeyNew === "") {
      return { error: `Destination ${label}: an API key is required — paste one to set it.` };
    }
    const vid = Number(d.vid);
    const aid = Number(d.aid);
    const lid = Number(d.lid);
    if (
      d.vid.trim() === "" ||
      d.aid.trim() === "" ||
      d.lid.trim() === "" ||
      !Number.isFinite(vid) ||
      !Number.isFinite(aid) ||
      !Number.isFinite(lid)
    ) {
      return { error: `Destination ${label}: vid / aid / lid must all be numbers.` };
    }
    destinations.push({
      id: d.id,
      name: d.name.trim(),
      enabled: d.enabled,
      skip_if_called: d.skip_if_called,
      url: d.url.trim(),
      // Round-trip the stored ARN + masked hint unchanged (never mutated here);
      // send api_key_new ONLY when the operator pasted a new/replacement key.
      api_key_secret_arn: d.api_key_secret_arn.trim(),
      ...(d.api_key_hint !== undefined ? { api_key_hint: d.api_key_hint } : {}),
      ...(apiKeyNew !== "" ? { api_key_new: apiKeyNew } : {}),
      source: { vid, aid, lid },
    });
  }

  // Routing rules — the destination must resolve to a real destination; weight >= 0.
  const destIds = new Set(destinations.map((d) => d.id));
  const routing_rules: RoutingRule[] = [];
  for (const [i, r] of form.routing_rules.entries()) {
    if (!r.destination_id || !destIds.has(r.destination_id)) {
      return { error: `Routing rule ${i + 1}: pick a destination that exists.` };
    }
    const weight = Number(r.weight);
    if (r.weight.trim() === "" || !Number.isFinite(weight) || weight < 0) {
      return {
        error: `Routing rule ${i + 1}: weight must be zero or a positive number.`,
      };
    }
    routing_rules.push({ tier: r.tier, destination_id: r.destination_id, weight });
  }

  return {
    config: {
      enabled,
      wait_ms,
      max_age_ms,
      sales_per_min: spm,
      destinations,
      routing_rules,
      exclusive_buyer_ids: [...form.exclusive_buyer_ids],
    },
  };
}

/** Map a config-save failure to a friendly message. The server surfaces
 *  invalid_band / invalid_routing / invalid_body as the ApiError `error` code;
 *  fall back to the raw message otherwise. */
function friendlyConfigError(err: unknown): string {
  if (err instanceof ApiError) {
    const code =
      err.body && typeof err.body === "object" && "error" in err.body
        ? (err.body as { error?: unknown }).error
        : undefined;
    if (code === "invalid_band") {
      return "Max age must be greater than wait — the eligibility band is empty as entered.";
    }
    if (code === "invalid_routing") {
      return "A routing rule points at a destination that doesn't exist — fix the rule or add the destination.";
    }
    if (code === "invalid_body") {
      return "Some config fields are invalid — check the destinations, rates, and rules.";
    }
    return err.message;
  }
  return err instanceof Error ? err.message : "Save failed";
}

export default function LeadSales() {
  // Config plane (the lead_sale_config singleton + the SALE tick cap).
  const [config, setConfig] = useState<LeadSaleConfigResponse | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Seen-buyers picker list (bounded td-calls scan) — loaded once; non-fatal.
  const [seen, setSeen] = useState<SeenBuyer[] | null>(null);
  const [seenLoading, setSeenLoading] = useState(true);
  const [seenError, setSeenError] = useState<string | null>(null);

  // Master on/off toggle state (drives setLeadSalesEnabled).
  const [toggling, setToggling] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [confirmingEnable, setConfirmingEnable] = useState(false);

  // Live plane (stats + recent sales), windowed + polled.
  const [presetSec, setPresetSec] = useState<number>(TODAY_PT); // default: Today (PT)
  const [custom, setCustom] = useState<{ start: string; end: string } | null>(null);
  const [stats, setStats] = useState<LeadSaleStats | null>(null);
  const [sales, setSales] = useState<LeadSaleRow[] | null>(null);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [liveLoading, setLiveLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [auto, setAuto] = useState(true);

  const statsOpts = useMemo(() => toStatsOpts(presetSec, custom), [presetSec, custom]);

  // --- Config load ---------------------------------------------------------
  const loadConfig = useCallback(() => {
    setConfigLoading(true);
    api
      .leadSalesConfig()
      .then((r) => {
        // Guard the shape so an edge/API error that fell through to HTML shows a
        // clean message instead of a cryptic undefined-read (the Dialer fix).
        if (!r || typeof r.config !== "object" || r.config === null) {
          throw new Error("Unexpected lead-sales config response — try reloading.");
        }
        setConfig(r);
        setForm(configToForm(r.config));
        setConfigError(null);
        setDirty(false);
      })
      .catch((e: unknown) =>
        setConfigError(e instanceof Error ? e.message : "Failed to load config"),
      )
      .finally(() => setConfigLoading(false));
  }, []);

  const loadSeen = useCallback(() => {
    setSeenLoading(true);
    setSeenError(null);
    api
      .seenBuyers()
      .then((b) => setSeen(b))
      .catch((e: unknown) =>
        setSeenError(e instanceof Error ? e.message : "Failed to load recent buyers"),
      )
      .finally(() => setSeenLoading(false));
  }, []);

  useEffect(() => {
    loadConfig();
    loadSeen();
  }, [loadConfig, loadSeen]);

  // --- Live load (stats + recent sales) ------------------------------------
  const load = useCallback(
    (showSpinner: boolean) => {
      if (showSpinner) setLiveLoading(true);
      Promise.all([api.leadSalesStats(statsOpts), api.leadSalesSales(SALES_LIMIT)])
        .then(([s, rows]) => {
          setStats(s);
          setSales(rows);
          setLiveError(null);
          setRefreshedAt(new Date());
        })
        .catch((err: unknown) => {
          setLiveError(err instanceof Error ? err.message : "Failed to load");
        })
        .finally(() => setLiveLoading(false));
    },
    [statsOpts],
  );

  // A ref so the poll timer always calls the latest fetch (current window)
  // without re-arming the interval on every filter change.
  const fetchRef = useRef<() => void>(() => {});
  useEffect(() => {
    fetchRef.current = () => load(false);
  }, [load]);

  // Re-fetch (with spinner) whenever the window changes.
  useEffect(() => {
    load(true);
  }, [load]);

  // Auto-refresh polling — pauses when the tab is hidden or auto is toggled off.
  useEffect(() => {
    if (!auto) return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchRef.current();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [auto]);

  // --- Master on/off toggle ------------------------------------------------
  // Off is immediate; On requires the inline confirm (setConfirmingEnable) first.
  const setEnabled = useCallback((next: boolean) => {
    setToggling(true);
    setToggleError(null);
    api
      .setLeadSalesEnabled(next)
      .then((cfg) => {
        setConfig((prev) =>
          prev ? { ...prev, config: cfg } : { config: cfg, tick_cap: TICK_CAP_FALLBACK },
        );
        setConfirmingEnable(false);
      })
      .catch((e: unknown) =>
        setToggleError(e instanceof Error ? e.message : "Failed to update"),
      )
      .finally(() => setToggling(false));
  }, []);

  // --- Config form ---------------------------------------------------------
  const update = useCallback(
    <K extends keyof FormState>(key: K, value: FormState[K]) => {
      setForm((f) => (f ? { ...f, [key]: value } : f));
      setDirty(true);
    },
    [],
  );

  const tickCap = config?.tick_cap ?? TICK_CAP_FALLBACK;

  // Live band guard (mirrors the server's 400 invalid_band): max_age must exceed
  // wait. Only flags when both parse — an empty field is caught by save() instead.
  const waitNum = form ? Number(form.waitMin) : NaN;
  const maxAgeNum = form ? Number(form.maxAgeMin) : NaN;
  const bandInvalid =
    Number.isFinite(waitNum) && Number.isFinite(maxAgeNum) && maxAgeNum <= waitNum;

  const save = useCallback(() => {
    if (!form || !config) return;
    const built = buildConfigFromForm(form, config.config.enabled, tickCap);
    if ("error" in built) {
      setConfigError(built.error);
      return;
    }
    setSaving(true);
    api
      .putLeadSalesConfig(built.config)
      .then((cfg) => {
        setConfig((prev) =>
          prev ? { ...prev, config: cfg } : { config: cfg, tick_cap: TICK_CAP_FALLBACK },
        );
        setForm(configToForm(cfg));
        setConfigError(null);
        setDirty(false);
        setSavedAt(new Date());
      })
      .catch((e: unknown) => setConfigError(friendlyConfigError(e)))
      .finally(() => setSaving(false));
  }, [form, config, tickCap]);

  const enabled = config?.config.enabled ?? false;
  const outcomes = stats?.outcomes;
  const transientSkips = stats?.transient_skips;

  // Join maps: destination_id → name (from the SAVED config the live proxy uses)
  // and buyer_id → name (from the seen-buyers list).
  const destNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of config?.config.destinations ?? []) m.set(d.id, d.name);
    return m;
  }, [config]);
  const buyerNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const b of seen ?? []) m.set(b.buyer_id, b.buyer_name);
    return m;
  }, [seen]);

  // Health hint — the "dark/ticking" line under the master toggle.
  let healthHint: string;
  if (!config) {
    healthHint = "Loading lead-sales status…";
  } else if (!enabled) {
    healthHint = "Lead Sales is OFF — no leads are being sold.";
  } else if ((stats?.considered ?? 0) === 0) {
    healthHint = "Lead Sales is ON but idle — nothing considered in this window yet.";
  } else {
    healthHint =
      "Lead Sales is ON — routing qualified leads to LeadExec (exclusive-sold leads withheld).";
  }

  const presetLabel = custom
    ? "Custom range"
    : PRESETS.find((p) => p.sec === presetSec)?.label ?? "Window";

  const selectStyle: CSSProperties = {
    background: tokens.color.bg2,
    color: tokens.color.fg0,
    border: `1px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.md,
    padding: `${tokens.space.sm}px ${tokens.space.md}px`,
    fontSize: tokens.font.size.sm,
    cursor: "pointer",
  };

  return (
    <div>
      <PageHeader
        title="Lead Sales"
        subtitle="Lead-sale proxy — exclusive-buyer gate → LeadExec, with tier routing"
        actions={
          <div
            style={{
              display: "flex",
              gap: tokens.space.sm,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <select
              value={custom ? "custom" : String(presetSec)}
              onChange={(e) => {
                if (e.target.value === "custom") {
                  const today = new Date().toISOString().slice(0, 10);
                  setCustom({ start: today, end: today });
                } else {
                  setCustom(null);
                  setPresetSec(Number(e.target.value));
                }
              }}
              style={selectStyle}
              aria-label="Time range"
            >
              {PRESETS.map((p) => (
                <option key={p.label} value={String(p.sec)}>
                  {p.label}
                </option>
              ))}
              <option value="custom">Custom range…</option>
            </select>
            {custom && (
              <>
                <input
                  type="date"
                  value={custom.start}
                  max={custom.end}
                  onChange={(e) => setCustom({ ...custom, start: e.target.value })}
                  style={selectStyle}
                  aria-label="Start date"
                />
                <span style={{ color: tokens.color.fg2 }}>→</span>
                <input
                  type="date"
                  value={custom.end}
                  min={custom.start}
                  onChange={(e) => setCustom({ ...custom, end: e.target.value })}
                  style={selectStyle}
                  aria-label="End date"
                />
              </>
            )}
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: tokens.color.fg2,
                fontSize: tokens.font.size.sm,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={auto}
                onChange={(e) => setAuto(e.target.checked)}
              />
              Auto
            </label>
            <button
              type="button"
              onClick={() => load(true)}
              disabled={liveLoading}
              style={buttonStyle("ghost", liveLoading)}
            >
              {liveLoading ? "…" : "Refresh"}
            </button>
          </div>
        }
      />

      {refreshedAt && (
        <div
          style={{
            color: tokens.color.fg2,
            fontSize: tokens.font.size.xs,
            marginBottom: tokens.space.md,
          }}
        >
          Updated{" "}
          {refreshedAt.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })}
          {auto ? ` · auto-refresh ${POLL_MS / 1000}s` : ""}
          {" · "}
          {presetLabel}
        </div>
      )}

      {/* Master on/off toggle — prominent, confirm-on-enable. */}
      <MasterToggle
        loading={configLoading && !config}
        enabled={enabled}
        healthHint={healthHint}
        toggling={toggling}
        confirming={confirmingEnable}
        toggleError={toggleError}
        onRequestEnable={() => setConfirmingEnable(true)}
        onConfirmEnable={() => setEnabled(true)}
        onCancelEnable={() => setConfirmingEnable(false)}
        onDisable={() => setEnabled(false)}
      />

      {liveError && !stats && (
        <Card style={{ marginBottom: tokens.space.xl }}>
          <EmptyState title="Failed to load stats" hint={liveError} />
        </Card>
      )}

      {/* Volume stat cards — the unique-lead headline: considered → sold, plus
          the SEPARATE do-not-resell held-back figure (protective, not an
          outcome). All three are distinct-lead counts (no per-tick inflation). */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: tokens.space.lg,
          marginBottom: tokens.space.xl,
        }}
      >
        {liveLoading && !stats ? (
          <>
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
          </>
        ) : stats ? (
          <>
            <StatCard
              label="Considered"
              value={fmtCount(stats.considered)}
              hint="unique leads looked at"
            />
            <StatCard
              label="Sold"
              value={fmtCount(stats.outcomes.sold)}
              hint="accepted by a buyer"
              emphasis
            />
            <StatCard
              label="Held back"
              value={fmtCount(stats.held_back.already_sold)}
              hint="Already sold (do-not-resell) — protected from re-sale, not an outcome"
            />
          </>
        ) : null}
      </div>

      {/* Outcomes / transient skips / by-destination / by-buyer breakdowns */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: tokens.space.lg,
          marginBottom: tokens.space.xl,
        }}
      >
        <Card title="Outcomes">
          {liveLoading && !outcomes ? (
            <Skeleton height={200} />
          ) : outcomes ? (
            <>
              <CardNote>
                One row per lead — its final decision. These are unique and
                reconcile: they sum to the leads decided this window.
              </CardNote>
              <Breakdown
                rows={[...OUTCOME_LABELS, ...WITZZY_OUTCOME_LABELS].map(
                  ({ key, label, tone }) => ({
                    label,
                    value: outcomes[key] ?? 0,
                    ...(tone ? { tone } : {}),
                  }),
                )}
              />
            </>
          ) : (
            <EmptyState title="No sale outcomes in this window" />
          )}
        </Card>

        <Card title="Other skips (transient)">
          {liveLoading && !transientSkips ? (
            <Skeleton height={200} />
          ) : transientSkips ? (
            <>
              <CardNote>
                Non-final — the lead is retried on a later tick. Deduped per lead
                (blocked &amp; no-route live under Outcomes, not here).
              </CardNote>
              <Breakdown
                rows={TRANSIENT_SKIP_LABELS.map(({ key, label }) => ({
                  label,
                  value: transientSkips[key],
                }))}
              />
            </>
          ) : (
            <EmptyState title="No skips in this window" />
          )}
        </Card>

        <Card title="By destination">
          {liveLoading && !stats ? (
            <Skeleton height={120} />
          ) : stats && stats.by_destination.length > 0 ? (
            <Breakdown
              rows={stats.by_destination.map((d) => ({
                label: destNameById.get(d.destination_id) ?? d.destination_id,
                value: d.count,
              }))}
            />
          ) : (
            <EmptyState title="No sales by destination in this window" />
          )}
        </Card>

        <Card title="Blocked by exclusive buyer">
          {liveLoading && !stats ? (
            <Skeleton height={120} />
          ) : stats && stats.by_buyer.length > 0 ? (
            <Breakdown
              rows={stats.by_buyer.map((b) => ({
                label: buyerNameById.get(b.buyer_id) ?? `#${b.buyer_id}`,
                value: b.count,
                tone: "warning" as PillTone,
              }))}
            />
          ) : (
            <EmptyState title="No exclusive blocks in this window" />
          )}
        </Card>
      </div>

      {/* Recent sales table */}
      <Card padding={0} style={{ marginBottom: tokens.space.xl }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: tokens.space.lg,
            borderBottom: `1px solid ${tokens.color.border}`,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: tokens.font.size.md }}>Recent sales</div>
            <div style={{ color: tokens.color.fg2, fontSize: tokens.font.size.xs }}>
              {sales ? `${sales.length} most recent` : "loading…"}
              {sales && sales.length >= SALES_LIMIT ? ` (capped at ${SALES_LIMIT})` : ""}
            </div>
          </div>
        </div>

        {liveLoading && !sales ? (
          <div style={{ padding: tokens.space.lg }}>
            <Skeleton height={36} />
            <div style={{ height: 8 }} />
            <Skeleton height={36} />
            <div style={{ height: 8 }} />
            <Skeleton height={36} />
          </div>
        ) : sales && sales.length === 0 ? (
          <EmptyState title="No sales yet" hint="Recent sales appear here once the proxy sells." />
        ) : sales ? (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                fontSize: tokens.font.size.sm,
                whiteSpace: "nowrap",
              }}
            >
              <thead>
                <tr>
                  <Th>Date / time</Th>
                  <Th>Name</Th>
                  <Th>Tier</Th>
                  <Th>Status</Th>
                  <Th>Destination</Th>
                  <Th>LeadExec id</Th>
                  <Th>Exclusive buyer</Th>
                  <Th>Failure reason</Th>
                </tr>
              </thead>
              <tbody>
                {sales.map((s, idx) => {
                  const name = `${s.first_name} ${s.last_name}`.trim();
                  const dest = s.destination_id
                    ? destNameById.get(s.destination_id) ?? s.destination_id
                    : null;
                  return (
                    <tr
                      key={`${s.phone_masked}-${s.sold_at_ms}-${idx}`}
                      style={{
                        background: idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
                      }}
                    >
                      <Td mono>{fmtSaleTime(s.sold_at_ms)}</Td>
                      <Td>{name || "—"}</Td>
                      <Td>{prettyTier(s.tier)}</Td>
                      <Td>
                        <Pill tone={statusTone(s.status)}>{s.status || "—"}</Pill>
                      </Td>
                      <Td>{dest || "—"}</Td>
                      <Td mono>{s.leadexec_lead_id != null ? String(s.leadexec_lead_id) : "—"}</Td>
                      <Td>{s.exclusive_buyer_name || "—"}</Td>
                      <Td>{s.failure_reason || "—"}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>

      {/* Config form — one Save persists the whole config (knobs + arrays). */}
      <Card
        title="Operating config"
        action={
          <div style={{ display: "flex", gap: tokens.space.sm, alignItems: "center" }}>
            {savedAt && (
              <span style={{ color: tokens.color.tealLight, fontSize: tokens.font.size.xs }}>
                Saved{" "}
                {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
            <button
              type="button"
              onClick={save}
              disabled={saving || !dirty || !form}
              style={buttonStyle("primary", saving || !dirty || !form)}
            >
              {saving ? "Saving…" : dirty ? "Save" : "Saved"}
            </button>
          </div>
        }
        style={{ marginBottom: tokens.space.xl }}
      >
        {configError && (
          <div
            role="alert"
            style={{
              marginBottom: tokens.space.lg,
              padding: `${tokens.space.sm}px ${tokens.space.md}px`,
              borderRadius: tokens.radius.sm,
              background: tokens.color.dangerBg,
              border: `1px solid ${tokens.color.dangerBorder}`,
              color: tokens.color.danger,
              fontSize: tokens.font.size.sm,
            }}
          >
            {configError}
          </div>
        )}

        {configLoading && !form ? (
          <>
            <Skeleton height={20} width={160} />
            <div style={{ height: tokens.space.md }} />
            <Skeleton height={36} />
            <div style={{ height: tokens.space.md }} />
            <Skeleton height={36} />
          </>
        ) : form ? (
          <>
            <p
              style={{
                color: tokens.color.fg2,
                fontSize: tokens.font.size.sm,
                marginTop: 0,
              }}
            >
              These knobs are the proxy's real control surface — saved to the
              config the live proxy reads each tick. The on/off master switch is
              at the top of the page; it (not this form) owns whether selling is
              live.
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: tokens.space.lg,
                alignItems: "start",
              }}
            >
              {/* Sales per minute */}
              <div>
                <FieldLabel
                  htmlFor="ls-sales-per-min"
                  hint={`sells per tick — capped at ${tickCap}`}
                >
                  Sales / min
                </FieldLabel>
                <input
                  id="ls-sales-per-min"
                  type="number"
                  min="1"
                  max={tickCap}
                  value={form.salesPerMin}
                  onChange={(e) => update("salesPerMin", e.target.value)}
                  style={inputStyle}
                  aria-label="Sales per minute"
                />
              </div>
            </div>

            {/* Advanced disclosure — the timing band (guarded + settle warning). */}
            <div style={{ marginTop: tokens.space.lg }}>
              <AdvancedBand
                waitMin={form.waitMin}
                maxAgeMin={form.maxAgeMin}
                bandInvalid={bandInvalid}
                onWaitChange={(v) => update("waitMin", v)}
                onMaxAgeChange={(v) => update("maxAgeMin", v)}
              />
            </div>

            {/* Destinations editor */}
            <SubHeading
              title="Sell destinations"
              hint="LeadExec insert endpoints. Paste each destination's API key — it's stored securely in Secrets Manager; only a masked hint is shown here, never the key value."
            />
            <DestinationsEditor
              value={form.destinations}
              onChange={(next) => update("destinations", next)}
              disabled={saving}
            />

            {/* Routing rules editor */}
            <SubHeading
              title="Routing rules"
              hint="Per debt tier, weighted-random across the matching destinations. No matching rule → the lead is 'no_route' (not sold)."
            />
            <RoutingRulesEditor
              value={form.routing_rules}
              destinations={form.destinations}
              onChange={(next) => update("routing_rules", next)}
              disabled={saving}
            />

            {/* Exclusive buyers editor */}
            <SubHeading
              title="Exclusive buyers"
              hint="TrackDrive buyers marked exclusive. A lead whose phone was sold to one of these (revenue > 0) is withheld from LeadExec."
            />
            <ExclusiveBuyersEditor
              value={form.exclusive_buyer_ids}
              seen={seen}
              seenLoading={seenLoading}
              seenError={seenError}
              onChange={(next) => update("exclusive_buyer_ids", next)}
              onReloadSeen={loadSeen}
              disabled={saving}
            />
          </>
        ) : null}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Master on/off toggle — prominent status + a deliberate confirm to enable.
// Off is immediate (no confirm); On reveals the warning + confirm buttons.
// ---------------------------------------------------------------------------

function MasterToggle({
  loading,
  enabled,
  healthHint,
  toggling,
  confirming,
  toggleError,
  onRequestEnable,
  onConfirmEnable,
  onCancelEnable,
  onDisable,
}: {
  loading: boolean;
  enabled: boolean;
  healthHint: string;
  toggling: boolean;
  confirming: boolean;
  toggleError: string | null;
  onRequestEnable: () => void;
  onConfirmEnable: () => void;
  onCancelEnable: () => void;
  onDisable: () => void;
}) {
  return (
    <Card
      style={{
        marginBottom: tokens.space.xl,
        borderColor: enabled ? "rgba(0,212,170,0.28)" : tokens.color.border,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: tokens.space.lg,
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.space.md,
            }}
          >
            <span
              style={{
                fontWeight: 700,
                fontSize: tokens.font.size.lg,
                color: tokens.color.fg0,
              }}
            >
              Lead-sale proxy
            </span>
            {loading ? (
              <Pill tone="neutral">…</Pill>
            ) : enabled ? (
              <Pill tone="success">On</Pill>
            ) : (
              <Pill tone="neutral">Off</Pill>
            )}
          </div>
          <p
            style={{
              margin: `${tokens.space.sm}px 0 0`,
              color: tokens.color.fg1,
              fontSize: tokens.font.size.sm,
            }}
          >
            {healthHint}
          </p>
        </div>

        <div style={{ display: "flex", gap: tokens.space.sm, alignItems: "center" }}>
          {toggling ? (
            <button type="button" disabled style={buttonStyle("ghost", true)}>
              Working…
            </button>
          ) : enabled ? (
            <button
              type="button"
              onClick={onDisable}
              aria-label="Turn Lead Sales off"
              style={buttonStyle("danger")}
            >
              Turn Off
            </button>
          ) : confirming ? (
            <>
              <button
                type="button"
                onClick={onConfirmEnable}
                aria-label="Confirm — start selling live leads"
                style={buttonStyle("primary")}
              >
                Yes, start selling live
              </button>
              <button type="button" onClick={onCancelEnable} style={buttonStyle("ghost")}>
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onRequestEnable}
              aria-label="Turn Lead Sales on"
              disabled={loading}
              style={buttonStyle("primary", loading)}
            >
              Turn On
            </button>
          )}
        </div>
      </div>

      {confirming && !enabled && (
        <div
          role="alert"
          style={{
            marginTop: tokens.space.lg,
            padding: `${tokens.space.md}px ${tokens.space.lg}px`,
            borderRadius: tokens.radius.md,
            background: tokens.color.dangerBg,
            border: `1px solid ${tokens.color.dangerBorder}`,
            color: tokens.color.danger,
            fontSize: tokens.font.size.sm,
          }}
        >
          <strong>Are you sure — this starts selling live leads to LeadExec.</strong>{" "}
          Turning the proxy on posts qualifying leads to your configured
          destinations on the next tick (real leads, real revenue + contract
          exposure). Leads sold to an exclusive buyer are withheld; every other
          eligible lead is routed and sold once. Off is immediate; on is deliberate.
        </div>
      )}

      {toggleError && (
        <div
          role="alert"
          style={{
            marginTop: tokens.space.md,
            color: tokens.color.danger,
            fontSize: tokens.font.size.sm,
          }}
        >
          {toggleError}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Advanced disclosure — the eligibility band (wait / max_age) in MINUTES, with
// the 5-minute settle warning + the client-side max_age>wait guard. Collapsed
// by default so the common config stays uncluttered.
// ---------------------------------------------------------------------------

function AdvancedBand({
  waitMin,
  maxAgeMin,
  bandInvalid,
  onWaitChange,
  onMaxAgeChange,
}: {
  waitMin: string;
  maxAgeMin: string;
  bandInvalid: boolean;
  onWaitChange: (v: string) => void;
  onMaxAgeChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div
      style={{
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.md,
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: tokens.space.sm,
          padding: `${tokens.space.md}px ${tokens.space.lg}px`,
          background: "transparent",
          border: "none",
          color: tokens.color.fg0,
          fontSize: tokens.font.size.base,
          fontWeight: 600,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span>Advanced — timing band</span>
        <span style={{ color: tokens.color.fg2, fontSize: tokens.font.size.sm }}>
          {open ? "Hide" : "Show"}
        </span>
      </button>

      {open && (
        <div
          style={{
            padding: `0 ${tokens.space.lg}px ${tokens.space.lg}px`,
            display: "flex",
            flexDirection: "column",
            gap: tokens.space.md,
          }}
        >
          <div
            role="note"
            style={{
              padding: `${tokens.space.sm}px ${tokens.space.md}px`,
              borderRadius: tokens.radius.sm,
              background: "rgba(212,168,83,0.12)",
              border: "1px solid rgba(212,168,83,0.40)",
              color: tokens.color.warning,
              fontSize: tokens.font.size.xs,
            }}
          >
            The <strong>5-minute settle risk</strong>: TrackDrive revenue/buyer
            can post minutes AFTER a call. Too short a wait may sell a lead a
            buyer is about to buy exclusively (a contract breach the exclusive
            gate can't yet see). Widen the wait to settle before selling; keep
            max age tight so leads don't age into stale sells.
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: tokens.space.lg,
            }}
          >
            <div>
              <FieldLabel
                htmlFor="ls-wait-min"
                hint="minutes to wait after the form-fill before a lead is eligible (settle window)"
              >
                Wait (minutes)
              </FieldLabel>
              <input
                id="ls-wait-min"
                type="number"
                min="1"
                step="1"
                value={waitMin}
                onChange={(e) => onWaitChange(e.target.value)}
                style={inputStyle}
                aria-label="Wait (minutes)"
              />
            </div>
            <div>
              <FieldLabel
                htmlFor="ls-max-age-min"
                hint="minutes after the form-fill past which a lead is too stale to sell"
              >
                Max age (minutes)
              </FieldLabel>
              <input
                id="ls-max-age-min"
                type="number"
                min="1"
                step="1"
                value={maxAgeMin}
                onChange={(e) => onMaxAgeChange(e.target.value)}
                style={{
                  ...inputStyle,
                  borderColor: bandInvalid
                    ? tokens.color.dangerBorder
                    : tokens.color.borderMid,
                }}
                aria-label="Max age (minutes)"
              />
            </div>
          </div>

          {bandInvalid && (
            <div
              role="alert"
              style={{ color: tokens.color.danger, fontSize: tokens.font.size.xs }}
            >
              Max age must be greater than wait — the band is empty as entered, so
              the proxy would sell nobody. Fix this before saving.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Destinations editor — add/remove LeadExec sell destinations. Each carries a
// stable id (routing rules reference it), a name, an enabled flag, a
// skip_if_called flag (only buy leads that haven't called TrackDrive), the POST
// url, the api-key (paste-only: a write-only api_key_new + the round-tripped
// stored ARN / masked hint — never the key value), and the source (vid/aid/lid).
// ---------------------------------------------------------------------------

function DestinationsEditor({
  value,
  onChange,
  disabled,
}: {
  value: DestDraft[];
  onChange: (next: DestDraft[]) => void;
  disabled: boolean;
}) {
  const add = (): void => onChange([...value, newDestDraft()]);
  const remove = (id: string): void => onChange(value.filter((d) => d.id !== id));
  const patch = (id: string, p: Partial<DestDraft>): void =>
    onChange(value.map((d) => (d.id === id ? { ...d, ...p } : d)));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.md }}>
      {value.length === 0 && (
        <div style={{ color: tokens.color.fg2, fontSize: tokens.font.size.sm }}>
          No destinations yet — add one to start routing sales.
        </div>
      )}

      {value.map((d, idx) => (
        <div
          key={d.id}
          style={{
            border: `1px solid ${tokens.color.border}`,
            borderRadius: tokens.radius.md,
            padding: tokens.space.lg,
            display: "flex",
            flexDirection: "column",
            gap: tokens.space.md,
            background: tokens.color.bg1,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: tokens.space.md,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: tokens.space.lg,
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  color: tokens.color.fg2,
                  fontSize: tokens.font.size.xs,
                  fontFamily: tokens.font.mono,
                }}
              >
                #{idx + 1}
              </span>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  color: tokens.color.fg1,
                  fontSize: tokens.font.size.sm,
                  cursor: disabled ? "not-allowed" : "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={d.enabled}
                  disabled={disabled}
                  onChange={(e) => patch(d.id, { enabled: e.target.checked })}
                />
                Enabled
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  color: tokens.color.fg1,
                  fontSize: tokens.font.size.sm,
                  cursor: disabled ? "not-allowed" : "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={d.skip_if_called}
                  disabled={disabled}
                  onChange={(e) => patch(d.id, { skip_if_called: e.target.checked })}
                  aria-label="Lead Called TD — Don't Sell"
                />
                Lead Called TD — Don't Sell
              </label>
            </div>
            <button
              type="button"
              onClick={() => remove(d.id)}
              disabled={disabled}
              style={buttonStyle("ghost", disabled)}
            >
              Remove
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: tokens.space.md,
            }}
          >
            <div>
              <FieldLabel htmlFor={`ls-dest-name-${d.id}`}>Name</FieldLabel>
              <input
                id={`ls-dest-name-${d.id}`}
                type="text"
                value={d.name}
                disabled={disabled}
                onChange={(e) => patch(d.id, { name: e.target.value })}
                style={inputStyle}
                aria-label="Destination name"
              />
            </div>
            <div>
              <FieldLabel htmlFor={`ls-dest-url-${d.id}`}>POST URL</FieldLabel>
              <input
                id={`ls-dest-url-${d.id}`}
                type="text"
                value={d.url}
                disabled={disabled}
                onChange={(e) => patch(d.id, { url: e.target.value })}
                style={inputStyle}
                aria-label="Destination URL"
              />
            </div>
          </div>

          <div>
            <FieldLabel
              htmlFor={`ls-dest-key-${d.id}`}
              hint="stored securely; only a masked hint is shown"
            >
              API key
            </FieldLabel>
            {/* Read-only current-key state. A masked hint when we have one; else
                "on file" when only an ARN is stored (a key set before hints
                existed); else "none" for a brand-new destination. */}
            <div
              style={{
                color: tokens.color.fg2,
                fontSize: tokens.font.size.xs,
                fontFamily: tokens.font.mono,
                marginBottom: tokens.space.sm,
              }}
            >
              Current key: {d.api_key_hint || (d.api_key_secret_arn ? "on file" : "none")}
            </div>
            <input
              id={`ls-dest-key-${d.id}`}
              type="text"
              value={d.api_key_new}
              disabled={disabled}
              placeholder="paste to set / replace"
              onChange={(e) => patch(d.id, { api_key_new: e.target.value })}
              style={{ ...inputStyle, fontFamily: tokens.font.mono, fontSize: tokens.font.size.sm }}
              aria-label="API key"
              autoComplete="off"
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
              gap: tokens.space.md,
            }}
          >
            <div>
              <FieldLabel htmlFor={`ls-dest-vid-${d.id}`} hint="source vid">
                vid
              </FieldLabel>
              <input
                id={`ls-dest-vid-${d.id}`}
                type="number"
                value={d.vid}
                disabled={disabled}
                onChange={(e) => patch(d.id, { vid: e.target.value })}
                style={inputStyle}
                aria-label="Source vid"
              />
            </div>
            <div>
              <FieldLabel htmlFor={`ls-dest-aid-${d.id}`} hint="source aid">
                aid
              </FieldLabel>
              <input
                id={`ls-dest-aid-${d.id}`}
                type="number"
                value={d.aid}
                disabled={disabled}
                onChange={(e) => patch(d.id, { aid: e.target.value })}
                style={inputStyle}
                aria-label="Source aid"
              />
            </div>
            <div>
              <FieldLabel htmlFor={`ls-dest-lid-${d.id}`} hint="source lid">
                lid
              </FieldLabel>
              <input
                id={`ls-dest-lid-${d.id}`}
                type="number"
                value={d.lid}
                disabled={disabled}
                onChange={(e) => patch(d.id, { lid: e.target.value })}
                style={inputStyle}
                aria-label="Source lid"
              />
            </div>
          </div>
        </div>
      ))}

      <div>
        <button
          type="button"
          onClick={add}
          disabled={disabled}
          style={buttonStyle("subtle", disabled)}
        >
          + Add destination
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Routing-rules editor — add/remove tier→destination weighted rules.
// ---------------------------------------------------------------------------

function RoutingRulesEditor({
  value,
  destinations,
  onChange,
  disabled,
}: {
  value: RuleDraft[];
  destinations: DestDraft[];
  onChange: (next: RuleDraft[]) => void;
  disabled: boolean;
}) {
  const add = (): void =>
    onChange([
      ...value,
      {
        id: shortId(),
        tier: "any",
        destination_id: destinations[0]?.id ?? "",
        weight: "1",
      },
    ]);
  const remove = (id: string): void => onChange(value.filter((r) => r.id !== id));
  const patch = (id: string, p: Partial<RuleDraft>): void =>
    onChange(value.map((r) => (r.id === id ? { ...r, ...p } : r)));

  const selectStyle: CSSProperties = { ...inputStyle, cursor: "pointer" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.md }}>
      {destinations.length === 0 && (
        <div style={{ color: tokens.color.fg2, fontSize: tokens.font.size.sm }}>
          Add a destination first — a routing rule points at one.
        </div>
      )}

      {value.length === 0 && destinations.length > 0 && (
        <div style={{ color: tokens.color.fg2, fontSize: tokens.font.size.sm }}>
          No rules yet — without a matching rule a lead is 'no_route' (not sold).
        </div>
      )}

      {value.map((r, idx) => (
        <div
          key={r.id}
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr 1fr 100px auto",
            gap: tokens.space.md,
            alignItems: "end",
            border: `1px solid ${tokens.color.border}`,
            borderRadius: tokens.radius.md,
            padding: tokens.space.md,
            background: tokens.color.bg1,
          }}
        >
          <span
            style={{
              color: tokens.color.fg2,
              fontSize: tokens.font.size.xs,
              fontFamily: tokens.font.mono,
              paddingBottom: 10,
            }}
          >
            #{idx + 1}
          </span>
          <div>
            <FieldLabel htmlFor={`ls-rule-tier-${r.id}`}>Tier</FieldLabel>
            <select
              id={`ls-rule-tier-${r.id}`}
              value={r.tier}
              disabled={disabled}
              onChange={(e) => patch(r.id, { tier: e.target.value as LeadSaleRoutingTier })}
              style={selectStyle}
              aria-label="Rule tier"
            >
              {TIER_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <FieldLabel htmlFor={`ls-rule-dest-${r.id}`}>Destination</FieldLabel>
            <select
              id={`ls-rule-dest-${r.id}`}
              value={r.destination_id}
              disabled={disabled || destinations.length === 0}
              onChange={(e) => patch(r.id, { destination_id: e.target.value })}
              style={selectStyle}
              aria-label="Rule destination"
            >
              <option value="">— select —</option>
              {destinations.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name.trim() || "(unnamed)"}
                </option>
              ))}
            </select>
          </div>
          <div>
            <FieldLabel htmlFor={`ls-rule-weight-${r.id}`}>Weight</FieldLabel>
            <input
              id={`ls-rule-weight-${r.id}`}
              type="number"
              min="0"
              step="1"
              value={r.weight}
              disabled={disabled}
              onChange={(e) => patch(r.id, { weight: e.target.value })}
              style={inputStyle}
              aria-label="Rule weight"
            />
          </div>
          <button
            type="button"
            onClick={() => remove(r.id)}
            disabled={disabled}
            style={buttonStyle("ghost", disabled)}
          >
            Remove
          </button>
        </div>
      ))}

      <div>
        <button
          type="button"
          onClick={add}
          disabled={disabled || destinations.length === 0}
          style={buttonStyle("subtle", disabled || destinations.length === 0)}
        >
          + Add rule
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Exclusive-buyers editor — pick from the buyers seen on recent td-calls (name
// + revenue hint), or add a buyer_id manually (a buyer we haven't seen yet).
// The chosen buyer_ids become exclusive_buyer_ids.
// ---------------------------------------------------------------------------

function ExclusiveBuyersEditor({
  value,
  seen,
  seenLoading,
  seenError,
  onChange,
  onReloadSeen,
  disabled,
}: {
  value: number[];
  seen: SeenBuyer[] | null;
  seenLoading: boolean;
  seenError: string | null;
  onChange: (next: number[]) => void;
  onReloadSeen: () => void;
  disabled: boolean;
}) {
  const [manual, setManual] = useState("");

  const toggle = (id: number, checked: boolean): void =>
    onChange(
      checked ? Array.from(new Set([...value, id])) : value.filter((x) => x !== id),
    );
  const addManual = (): void => {
    const n = Number(manual);
    if (manual.trim() === "" || !Number.isFinite(n)) return;
    if (!value.includes(n)) onChange([...value, n]);
    setManual("");
  };

  const seenIds = new Set((seen ?? []).map((b) => b.buyer_id));
  const manualSelected = value.filter((id) => !seenIds.has(id));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.md }}>
      {seenLoading ? (
        <Skeleton height={88} />
      ) : (
        <>
          {seenError && (
            <div style={{ color: tokens.color.fg2, fontSize: tokens.font.size.xs }}>
              Couldn't load recent buyers ({seenError}) — you can still add a
              buyer_id manually below.{" "}
              <button
                type="button"
                onClick={onReloadSeen}
                style={{
                  background: "transparent",
                  border: "none",
                  color: tokens.color.tealLight,
                  cursor: "pointer",
                  padding: 0,
                  fontSize: tokens.font.size.xs,
                }}
              >
                Retry
              </button>
            </div>
          )}

          {seen && seen.length > 0 ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: tokens.space.sm,
              }}
            >
              {seen.map((b) => {
                const checked = value.includes(b.buyer_id);
                return (
                  <label
                    key={b.buyer_id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: tokens.space.sm,
                      cursor: disabled ? "not-allowed" : "pointer",
                      color: tokens.color.fg0,
                      fontSize: tokens.font.size.sm,
                      border: `1px solid ${tokens.color.border}`,
                      borderRadius: tokens.radius.sm,
                      padding: `${tokens.space.sm}px ${tokens.space.md}px`,
                      background: checked ? "rgba(0,212,170,0.08)" : "transparent",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={(e) => toggle(b.buyer_id, e.target.checked)}
                    />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ fontWeight: 600 }}>
                        {b.buyer_name || `Buyer #${b.buyer_id}`}
                      </span>
                      <span
                        style={{
                          display: "block",
                          color: tokens.color.fg2,
                          fontSize: tokens.font.size.xs,
                          fontFamily: tokens.font.mono,
                        }}
                      >
                        id {b.buyer_id}
                        {b.sample_revenue_cents != null
                          ? ` · ~${fmtCents(b.sample_revenue_cents)}`
                          : ""}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          ) : (
            !seenError && (
              <div style={{ color: tokens.color.fg2, fontSize: tokens.font.size.sm }}>
                No buyers seen on recent calls — add a buyer_id manually below.
              </div>
            )
          )}
        </>
      )}

      {/* Manually-added ids not present in the seen list. */}
      {manualSelected.length > 0 && (
        <div style={{ display: "flex", gap: tokens.space.sm, flexWrap: "wrap" }}>
          {manualSelected.map((id) => (
            <span
              key={id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "3px 6px 3px 10px",
                borderRadius: tokens.radius.pill,
                background: "rgba(0,212,170,0.08)",
                border: "1px solid rgba(0,212,170,0.30)",
                color: tokens.color.tealLight,
                fontSize: tokens.font.size.xs,
                fontFamily: tokens.font.mono,
              }}
            >
              id {id} (manual)
              <button
                type="button"
                onClick={() => toggle(id, false)}
                disabled={disabled}
                aria-label={`Remove buyer ${id}`}
                style={{
                  background: "transparent",
                  border: "none",
                  color: tokens.color.tealLight,
                  cursor: disabled ? "not-allowed" : "pointer",
                  padding: 0,
                  fontSize: tokens.font.size.sm,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Manual add. */}
      <div style={{ display: "flex", gap: tokens.space.sm, alignItems: "center" }}>
        <input
          type="number"
          value={manual}
          disabled={disabled}
          placeholder="buyer_id"
          onChange={(e) => setManual(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addManual();
            }
          }}
          style={{ ...inputStyle, maxWidth: 160 }}
          aria-label="Manual buyer id"
        />
        <button
          type="button"
          onClick={addManual}
          disabled={disabled || manual.trim() === ""}
          style={buttonStyle("subtle", disabled || manual.trim() === "")}
        >
          Add buyer id
        </button>
        <span style={{ color: tokens.color.fg2, fontSize: tokens.font.size.xs }}>
          {value.length} exclusive {value.length === 1 ? "buyer" : "buyers"} selected
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SubHeading — a divider + label for a config sub-section.
// ---------------------------------------------------------------------------

function SubHeading({ title, hint }: { title: string; hint?: string }) {
  return (
    <div
      style={{
        marginTop: tokens.space.xl,
        marginBottom: tokens.space.md,
        paddingTop: tokens.space.lg,
        borderTop: `1px solid ${tokens.color.border}`,
      }}
    >
      <div style={{ fontWeight: 700, fontSize: tokens.font.size.md, color: tokens.color.fg0 }}>
        {title}
      </div>
      {hint && (
        <div
          style={{
            color: tokens.color.fg2,
            fontSize: tokens.font.size.xs,
            marginTop: 2,
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CardNote — a small muted line above a Breakdown, explaining what the numbers
// mean (unique + reconciling vs transient). Keeps the reshaped stats panel's
// semantics legible without a heavier subtitle treatment.
// ---------------------------------------------------------------------------

function CardNote({ children }: { children: ReactNode }) {
  return (
    <p
      style={{
        margin: `0 0 ${tokens.space.md}px`,
        color: tokens.color.fg2,
        fontSize: tokens.font.size.xs,
        lineHeight: 1.5,
      }}
    >
      {children}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Breakdown — a compact label → count list used for the outcomes, transient
// skips, and by-destination / by-buyer cards. An optional tone tints the count.
// ---------------------------------------------------------------------------

function Breakdown({
  rows,
}: {
  rows: Array<{ label: string; value: number; tone?: PillTone }>;
}) {
  const toneColor: Record<PillTone, string> = {
    neutral: tokens.color.fg0,
    success: tokens.color.tealLight,
    warning: tokens.color.warning,
    danger: tokens.color.danger,
  };
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {rows.map((r, i) => (
        <div
          key={r.label}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: tokens.space.md,
            padding: `${tokens.space.sm}px 0`,
            borderBottom:
              i < rows.length - 1 ? `1px solid ${tokens.color.border}` : "none",
          }}
        >
          <span style={{ color: tokens.color.fg1, fontSize: tokens.font.size.sm }}>
            {r.label}
          </span>
          <span
            style={{
              fontFamily: tokens.font.mono,
              fontVariantNumeric: "tabular-nums",
              fontWeight: 700,
              color: r.tone ? toneColor[r.tone] : tokens.color.fg0,
            }}
          >
            {fmtCount(r.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small shared table + skeleton atoms (mirrors Dialer).
// ---------------------------------------------------------------------------

function SkeletonStat() {
  return (
    <div
      style={{
        background: tokens.color.bg2,
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.lg,
        padding: tokens.space.lg,
      }}
    >
      <Skeleton height={10} width="40%" />
      <div style={{ height: tokens.space.sm }} />
      <Skeleton height={28} width="70%" />
      <div style={{ height: tokens.space.xs }} />
      <Skeleton height={10} width="50%" />
    </div>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: ReactNode;
  align?: "left" | "right" | "center";
}) {
  return (
    <th
      style={{
        textAlign: align,
        padding: `${tokens.space.sm}px ${tokens.space.md}px`,
        color: tokens.color.fg2,
        fontWeight: 600,
        fontSize: tokens.font.size.xs,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        borderBottom: `1px solid ${tokens.color.border}`,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
  mono = false,
}: {
  children: ReactNode;
  align?: "left" | "right" | "center";
  mono?: boolean;
}) {
  return (
    <td
      style={{
        textAlign: align,
        padding: `${tokens.space.sm}px ${tokens.space.md}px`,
        color: tokens.color.fg1,
        borderBottom: `1px solid ${tokens.color.border}`,
        fontFamily: mono ? tokens.font.mono : undefined,
        fontSize: mono ? tokens.font.size.xs : undefined,
      }}
    >
      {children}
    </td>
  );
}
